import React, { useState, useEffect } from 'react';
import { Edit, Trash2, Eye, Plus, DollarSign, Grid3x3, ChevronLeft, AlertCircle, X, Loader, CheckCircle2, XCircle, Building2, Lightbulb, ExternalLink, List, TrendingUp, Camera } from 'lucide-react';
import { fetchCategories, createCategory, updateCategory, deleteCategory } from './api';
import CategoryDialog from './CategoryDialog';
import CategoryPhotoManagement from './CategoryPhotoManagement';
import { useAppTheme } from '../../contexts/ThemeContext';

const CategoryManagement = ({ currentApartment, handleBackToApartments, handleViewCategoryDetails, onApartmentDataChange, onViewAllUnits }) => {
  const { palette, Colors } = useAppTheme();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [openAddUnitsDialog, setOpenAddUnitsDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [viewMode, setViewMode] = useState(0);
  const [selectedCategoryForPhotos, setSelectedCategoryForPhotos] = useState(null);
  const [addUnitsForm, setAddUnitsForm] = useState({ categoryId: null, categoryName: '', addUnits: '' });

  useEffect(() => { if (currentApartment) loadCategories(); }, [currentApartment]);

  const loadCategories = async () => {
    try { setLoading(true); setCategories(await fetchCategories(currentApartment.id)); setError(null); }
    catch (err) { setError('Failed to load categories: ' + err.message); }
    finally { setLoading(false); }
  };

  const handleManageCategoryPhotos = (cat) => { setSelectedCategoryForPhotos(cat); setViewMode(1); };
  const handleBackToCategories     = ()    => { setViewMode(0); setSelectedCategoryForPhotos(null); };

  const handleSubmitCategory = async (payload) => {
    // payload is already built and validated by CategoryDialog
    try {
      setLoading(true);
      if (editingCategory) {
        await updateCategory(editingCategory.id, payload);
      } else {
        await createCategory(currentApartment.id, payload);
      }
      setOpenCategoryDialog(false); setEditingCategory(null); await loadCategories(); onApartmentDataChange();
    } catch (err) { setError((editingCategory ? 'Failed to update' : 'Failed to create') + ' category: ' + err.message); }
    finally { setLoading(false); }
  };

  const handleSubmitAddUnits = async () => {
    try {
      setLoading(true);
      await updateCategory(addUnitsForm.categoryId, { addUnits: parseInt(addUnitsForm.addUnits) });
      setOpenAddUnitsDialog(false); setAddUnitsForm({ categoryId: null, categoryName: '', addUnits: '' });
      await loadCategories(); onApartmentDataChange();
    } catch (err) { setError('Failed to add units: ' + err.message); }
    finally { setLoading(false); }
  };

  const handleDeleteCategory = async (id, name) => {
    if (window.confirm(`Delete ${name}? This will also delete all units in this category.`)) {
      try { setLoading(true); await deleteCategory(id); await loadCategories(); onApartmentDataChange(); }
      catch (err) { setError('Failed to delete category: ' + err.message); }
      finally { setLoading(false); }
    }
  };

  const totalUnits    = categories.reduce((s, c) => s + c.totalUnits, 0);
  const totalOccupied = categories.reduce((s, c) => s + (c.totalUnits - c.vacantUnits), 0);
  const totalVacant   = totalUnits - totalOccupied;
  const avgOccupancy  = totalUnits > 0 ? Math.round((totalOccupied / totalUnits) * 100) : 0;

  const P   = Colors.primary.main;
  const bg  = palette.background;
  const txt = palette.text;
  const bdr = palette.border;

  return (
    <div className="min-h-screen">
      {viewMode === 0 ? (
        <>
          {/* Header */}
          <div className="rounded-xl shadow-sm p-6 mb-6" style={{ backgroundColor: bg.secondary, border: `1px solid ${bdr}` }}>
            <button onClick={handleBackToApartments}
              className="flex items-center gap-2 mb-4 font-medium transition group px-3 py-1.5 rounded-md"
              style={{ color: txt.secondary, border: `1px solid ${bdr}` }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = bg.tertiary}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
              <ChevronLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" /> Back to All Properties
            </button>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h2 className="text-xl md:text-2xl font-bold mb-1" style={{ color: P }}>{currentApartment.name}</h2>
                <p className="text-sm" style={{ color: txt.secondary }}>Available Categories</p>
              </div>
              <button onClick={() => setOpenCategoryDialog(true)}
                className="text-white font-semibold px-6 py-2.5 rounded-lg flex items-center gap-2 shadow-sm transition"
                style={{ backgroundColor: P }}>
                <Plus className="w-5 h-5" /> Add Category
              </button>
            </div>
          </div>

          {/* Stats */}
          {categories.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              {/* Total Categories */}
              <div className="rounded-xl p-6 text-white shadow-lg"
                style={{ background: `linear-gradient(135deg, ${Colors.accent.purple}, #C084FC)` }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium mb-1 text-white/90">Total Categories</p>
                    <h3 className="text-4xl font-bold">{categories.length}</h3>
                  </div>
                  <div className="bg-white/20 p-3 rounded-lg"><Grid3x3 className="w-8 h-8" /></div>
                </div>
              </div>
          
              {/* Total Units — with "View All Units" button */}
              <div className="rounded-xl p-6 text-white shadow-lg"
                style={{ background: `linear-gradient(135deg, ${Colors.primary.dark}, ${Colors.primary.main})` }}>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium mb-1 text-white/90">Total Units</p>
                    <h3 className="text-4xl font-bold">{totalUnits}</h3>
                    <p className="mt-2 text-sm text-white/80">{totalOccupied} Occupied • {totalVacant} Vacant</p>
                  </div>
                  <div className="bg-white/20 p-3 rounded-lg"><Building2 className="w-8 h-8" /></div>
                </div>
                {/* ── View All Units button ── */}
                <button
                  onClick={onViewAllUnits}
                  className="mt-4 w-full flex items-center justify-center gap-2 text-sm font-semibold
                            py-2 rounded-lg transition"
                  style={{ backgroundColor: 'rgba(255,255,255,0.20)', color: '#fff' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.32)'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.20)'}>
                  <List className="w-4 h-4" />
                  View All Units
                </button>
              </div>
          
              {/* Avg Occupancy */}
              <div className="rounded-xl p-6 text-white shadow-lg"
                style={{ background: `linear-gradient(135deg, ${avgOccupancy >= 80 ? Colors.status.success : avgOccupancy >= 60 ? Colors.status.warning : Colors.status.error}, ${avgOccupancy >= 80 ? '#34D399' : avgOccupancy >= 60 ? '#FCD34D' : '#F87171'})` }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium mb-1 text-white/90">Avg Occupancy</p>
                    <h3 className="text-4xl font-bold">{avgOccupancy}%</h3>
                  </div>
                  <div className="bg-white/20 p-3 rounded-lg"><TrendingUp className="w-8 h-8" /></div>
                </div>
              </div>
            </div>
          )}

          {/* Tips */}
          <div className="rounded-xl p-5 mb-6 shadow-sm" style={{ backgroundColor: `${P}08`, border: `1px solid ${P}25` }}>
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg" style={{ backgroundColor: `${P}15` }}>
                <Lightbulb className="w-5 h-5" style={{ color: P }} />
              </div>
              <div className="flex-1">
                <h3 className="font-bold mb-2 text-lg" style={{ color: txt.primary }}>Tips for Managing Categories</h3>
                <ul className="text-sm space-y-2" style={{ color: txt.secondary }}>
                  {[['Group similar units', 'Add categories and group them according to similar units (e.g., all one-bedroom units)'],
                    ['Create distinct categories', 'Create new categories for distinct groups of units with different features or pricing'],
                    ['Add descriptions', 'Describing features provides additional benefits for house hunters browsing your properties']].map(([title, desc]) => (
                    <li key={title} className="flex items-start gap-2">
                      <span className="font-bold mt-0.5" style={{ color: P }}>•</span>
                      <span><strong>{title}:</strong> {desc}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="rounded-xl p-4 mb-6 flex items-start gap-3 shadow-sm" style={{ backgroundColor: `${Colors.status.error}10`, border: `1px solid ${Colors.status.error}40` }}>
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: Colors.status.error }} />
              <p className="flex-1 font-medium" style={{ color: Colors.status.error }}>{error}</p>
              <button onClick={() => setError(null)} style={{ color: Colors.status.error }}><X className="w-5 h-5" /></button>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="rounded-xl shadow-sm p-12 text-center" style={{ backgroundColor: bg.secondary, border: `1px solid ${bdr}` }}>
              <Loader className="w-12 h-12 mx-auto mb-4 animate-spin" style={{ color: P }} />
              <p style={{ color: txt.secondary }}>Loading categories...</p>
            </div>
          )}

          {/* Category Cards */}
          {!loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {categories.map(category => {
                const occupied = category.totalUnits - category.vacantUnits;
                const rate     = category.totalUnits > 0 ? Math.round((occupied / category.totalUnits) * 100) : 0;
                const statusColor = rate >= 80 ? Colors.status.success : rate >= 60 ? Colors.status.warning : Colors.status.error;

                return (
                  <div key={category.id} className="rounded-xl shadow-sm overflow-hidden transition-all duration-300 hover:-translate-y-1"
                    style={{ backgroundColor: bg.secondary, border: `1px solid ${bdr}` }}
                    onMouseEnter={e => e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.15)'}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = ''}>

                    {/* Card Header */}
                    <div className="p-5 text-white" style={{ background: `linear-gradient(135deg, ${Colors.accent.purple}, #C084FC)` }}>
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-xl font-bold flex-1">{category.name}</h3>
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold shadow-sm"
                          style={{ backgroundColor: statusColor, color: '#fff' }}>
                          {rate}% Full
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-white/80">
                        <DollarSign className="w-4 h-4" />
                        <p className="text-lg font-semibold">KES {category.baseRent.toLocaleString()}/month</p>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="p-5" style={{ borderBottom: `1px solid ${bdr}`, backgroundColor: bg.tertiary }}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold" style={{ color: txt.secondary }}>Unit Status</span>
                        <span className="text-sm font-bold" style={{ color: txt.primary }}>{occupied} / {category.totalUnits}</span>
                      </div>
                      <div className="w-full rounded-full h-2.5 mb-2 overflow-hidden" style={{ backgroundColor: bdr }}>
                        <div className="h-2.5 rounded-full transition-all duration-500" style={{ width: `${rate}%`, backgroundColor: statusColor }}></div>
                      </div>
                      <div className="flex items-center justify-between text-xs" style={{ color: txt.secondary }}>
                        <div className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" style={{ color: Colors.status.success }} /><span className="font-medium">{occupied} Occupied</span></div>
                        <div className="flex items-center gap-1"><XCircle className="w-3.5 h-3.5" style={{ color: Colors.status.error }} /><span className="font-medium">{category.vacantUnits} Vacant</span></div>
                      </div>
                    </div>

                    {/* Description */}
                    {category.description?.length > 0 && (
                      <div className="p-5" style={{ borderBottom: `1px solid ${bdr}` }}>
                        <div className="flex items-center gap-2 mb-3">
                          <div className="p-1.5 rounded" style={{ backgroundColor: `${Colors.accent.purple}20` }}>
                            <List className="w-4 h-4" style={{ color: Colors.accent.purple }} />
                          </div>
                          <h4 className="text-sm font-bold" style={{ color: txt.primary }}>Features & Amenities</h4>
                        </div>
                        <div className="space-y-2">
                          {category.description.slice(0, 3).map((point, i) => (
                            <div key={i} className="flex items-start gap-2.5">
                              <div className="mt-1.5"><div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: Colors.accent.purple }}></div></div>
                              <p className="text-sm leading-relaxed flex-1" style={{ color: txt.secondary }}>{point}</p>
                            </div>
                          ))}
                          {category.description.length > 3 && <p className="text-xs ml-4 font-medium" style={{ color: txt.muted }}>+{category.description.length - 3} more features</p>}
                        </div>
                      </div>
                    )}

                    {/* URL */}
                    {category.url && (
                      <div className="px-5 py-3" style={{ borderBottom: `1px solid ${bdr}`, backgroundColor: `${P}08` }}>
                        <a href={category.url} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm font-medium transition group" style={{ color: P }}>
                          <ExternalLink className="w-4 h-4 group-hover:scale-110 transition-transform" /> View Additional Details
                        </a>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="px-5 py-3.5" style={{ backgroundColor: bg.tertiary }}>
                      <div className="flex items-center justify-between">
                        <button onClick={() => handleViewCategoryDetails(currentApartment.id, category.id)}
                          className="flex items-center gap-2 font-semibold text-sm transition group" style={{ color: Colors.accent.purple }}>
                          <Eye className="w-4 h-4" /><span>View Units</span>
                          <ChevronLeft className="w-4 h-4 rotate-180 group-hover:translate-x-1 transition-transform" />
                        </button>
                        <div className="flex items-center gap-1">
                          <button onClick={() => handleManageCategoryPhotos(category)} className="p-2 rounded-lg transition" style={{ color: Colors.accent.purple }} title="Manage Photos"
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = `${Colors.accent.purple}15`}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                            <Camera className="w-4 h-4" />
                          </button>
                          <button onClick={() => { setAddUnitsForm({ categoryId: category.id, categoryName: category.name, addUnits: '' }); setOpenAddUnitsDialog(true); }}
                            className="p-2 rounded-lg transition" style={{ color: Colors.status.success }} title="Add More Units"
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = `${Colors.status.success}15`}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                            <Plus className="w-4 h-4" />
                          </button>
                          <button onClick={() => { setEditingCategory(category); setOpenCategoryDialog(true); }}
                            className="p-2 rounded-lg transition" style={{ color: P }} title="Edit"
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = `${P}15`}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                            <Edit className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDeleteCategory(category.id, category.name)}
                            className="p-2 rounded-lg transition" style={{ color: Colors.status.error }} title="Delete"
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = `${Colors.status.error}15`}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Empty state */}
          {!loading && categories.length === 0 && (
            <div className="text-center py-16 rounded-xl shadow-sm" style={{ backgroundColor: bg.secondary, border: `1px solid ${bdr}` }}>
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: bg.tertiary }}>
                <Grid3x3 className="w-10 h-10" style={{ color: txt.muted }} />
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ color: txt.primary }}>No categories yet</h3>
              <p className="mb-6 max-w-md mx-auto" style={{ color: txt.secondary }}>Get started by adding your first category to organize your units</p>
              <button onClick={() => setOpenCategoryDialog(true)}
                className="text-white px-6 py-3 rounded-lg font-semibold inline-flex items-center gap-2 transition shadow-sm" style={{ backgroundColor: P }}>
                <Plus className="w-5 h-5" /> Add Your First Category
              </button>
            </div>
          )}

          {/* Category Dialog */}
          <CategoryDialog open={openCategoryDialog} onClose={() => { setOpenCategoryDialog(false); setEditingCategory(null); }}
            onSubmit={handleSubmitCategory} category={editingCategory} loading={loading}
            propertyType={currentApartment?.propertyType || 'rental'} />

          {/* Add Units Dialog */}
          {openAddUnitsDialog && (
            <div className="fixed inset-0 flex items-center justify-center p-4 z-50" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
              <div className="rounded-xl shadow-xl max-w-md w-full" style={{ backgroundColor: bg.secondary, border: `1px solid ${bdr}` }}>
                <div className="text-white px-6 py-4 rounded-t-xl flex items-center justify-between"
                  style={{ background: `linear-gradient(135deg, ${Colors.status.success}, #34D399)` }}>
                  <h2 className="text-xl font-bold">Add More Units</h2>
                  <button onClick={() => { setOpenAddUnitsDialog(false); setAddUnitsForm({ categoryId: null, categoryName: '', addUnits: '' }); }}
                    className="text-white/80 hover:text-white transition"><X className="w-6 h-6" /></button>
                </div>
                <div className="p-6 space-y-4">
                  <div className="rounded-lg p-4" style={{ backgroundColor: `${P}10`, border: `1px solid ${P}30` }}>
                    <p className="text-sm" style={{ color: txt.secondary }}>
                      Adding units to <span className="font-bold" style={{ color: txt.primary }}>{addUnitsForm.categoryName}</span>.
                      New units will use the current base rent of <span className="font-bold" style={{ color: txt.primary }}>KES {categories.find(c => c.id === addUnitsForm.categoryId)?.baseRent.toLocaleString()}</span>.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: txt.secondary }}>Number of Units to Add *</label>
                    <input type="number" min="1" value={addUnitsForm.addUnits} onChange={e => setAddUnitsForm({ ...addUnitsForm, addUnits: e.target.value })}
                      placeholder="e.g., 5"
                      className="w-full px-4 py-2.5 rounded-lg focus:outline-none transition"
                      style={{ backgroundColor: bg.tertiary, color: txt.primary, border: `1px solid ${bdr}` }}
                      onFocus={e => { e.target.style.borderColor = Colors.status.success; e.target.style.boxShadow = `0 0 0 2px ${Colors.status.success}30`; }}
                      onBlur={e => { e.target.style.borderColor = bdr; e.target.style.boxShadow = ''; }} />
                  </div>
                </div>
                <div className="px-6 py-4 flex justify-end gap-3 rounded-b-xl" style={{ backgroundColor: bg.tertiary, borderTop: `1px solid ${bdr}` }}>
                  <button onClick={() => { setOpenAddUnitsDialog(false); setAddUnitsForm({ categoryId: null, categoryName: '', addUnits: '' }); }}
                    className="px-6 py-2.5 font-semibold rounded-lg transition"
                    style={{ backgroundColor: bg.secondary, border: `1px solid ${bdr}`, color: txt.primary }}>Cancel</button>
                  <button onClick={handleSubmitAddUnits} disabled={loading || !addUnitsForm.addUnits}
                    className="text-white px-6 py-2.5 rounded-lg font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                    style={{ backgroundColor: Colors.status.success }}>
                    {loading ? 'Adding...' : 'Add Units'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <CategoryPhotoManagement currentApartment={currentApartment} currentCategory={selectedCategoryForPhotos} handleBackToCategories={handleBackToCategories} />
      )}
    </div>
  );
};

export default CategoryManagement;